import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Package,
  Search,
  ChevronRight,
  Sparkles,
  ArrowLeft,
  Loader2,
  IndianRupee,
  MapPin,
  FileText,
  Tag,
} from "lucide-react";
import { getEquipmentItems, EquipmentItem } from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import {
  publishFromEquipment,
  BookingMode,
  PriceUnit,
  ListingStatus,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import {
  ModalOverlay,
  ModalShell as BaseModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
  CardLabel,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
  SearchControl,
  ClearButton,
  FieldInput,
  FieldTextArea,
  FieldSelect,
} from "../quotationMasterListShared";
import { Field, FieldGrid, Label, FormLabelIcon } from "../../../molecules/Dashboard/Member/memberFormStyles";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

interface PublishForm {
  price: string;
  price_unit: PriceUnit;
  booking_mode: BookingMode;
  location: string;
  status: ListingStatus;
  title: string;
  description: string;
}

const defaultForm: PublishForm = {
  price: "",
  price_unit: "per_day",
  booking_mode: "inquiry_only",
  location: "",
  status: "active",
  title: "",
  description: "",
};

type Step = "pick" | "configure";

const PublishFromEquipmentModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState<Step>("pick");
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EquipmentItem | null>(null);
  const [form, setForm] = useState<PublishForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getEquipmentItems({ page_size: 100, ownership_type: "rent" })
      .then((res) => {
        const items = res.results ?? [];
        return getEquipmentItems({ page_size: 100, ownership_type: "sale" })
          .then((saleRes) => {
            setEquipment([...items, ...(saleRes.results ?? [])]);
          })
          .catch(() => {
            setEquipment(items);
          });
      })
      .catch(() => setEquipment([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = equipment.filter(
    (e) =>
      !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.brand.toLowerCase().includes(search.toLowerCase())
  );

  const handlePick = (item: EquipmentItem) => {
    setSelected(item);
    setForm({
      ...defaultForm,
      title: item.name,
      price: item.purchase_price || "",
      price_unit: item.ownership_type === "rent" ? "per_day" : "fixed",
    });
    setStep("configure");
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    if (!form.price) {
      showErrorToast("Price is required");
      return;
    }
    setSubmitting(true);
    try {
      await publishFromEquipment(selected.id, {
        title: form.title || undefined,
        description: form.description || undefined,
        price: form.price,
        price_unit: form.price_unit,
        location: form.location || undefined,
        status: form.status,
      });
      showSuccessToast(`"${selected.name}" published to marketplace!`);
      onSuccess();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to publish equipment");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell $maxWidth={560} onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Marketplace
              </HeroBadge>
              <ModalTitle>
                {step === "pick" ? "Publish from Inventory" : "Configure Listing"}
              </ModalTitle>
              <ModalSubtitle>
                {step === "pick"
                  ? "Select rent or sale equipment from your inventory to list on the marketplace."
                  : `Set pricing and details for "${selected?.name}".`}
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        {step === "pick" && (
          <>
            <PickToolbar>
              <SearchControl>
                <Search size={16} />
                <input
                  placeholder="Search by name or brand..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <ClearButton type="button" onClick={() => setSearch("")}>
                    <X size={14} />
                  </ClearButton>
                )}
              </SearchControl>
            </PickToolbar>

            <EquipmentList>
              {loading ? (
                <EmptyState>
                  <Spinner size={28} color="#c94daa" />
                  <span>Loading equipment…</span>
                </EmptyState>
              ) : filtered.length === 0 ? (
                <EmptyState>
                  <Package size={32} strokeWidth={1.5} color="rgba(23,23,23,0.25)" />
                  <EmptyTitle>No equipment found</EmptyTitle>
                  <EmptyDesc>
                    {search
                      ? "Nothing matched your search. Try different keywords."
                      : 'Add equipment with ownership type "Rent" or "Sale" in your inventory first.'}
                  </EmptyDesc>
                </EmptyState>
              ) : (
                filtered.map((item) => (
                  <EquipmentRow key={item.id} type="button" onClick={() => handlePick(item)}>
                    <EquipIconBox>
                      <Package size={18} color="#7c4dff" />
                    </EquipIconBox>
                    <EquipInfo>
                      <EquipName>{item.name}</EquipName>
                      <EquipMeta>
                        {item.brand} · {item.model} ·
                        <TypeTag $type={item.ownership_type}>
                          {item.ownership_type === "rent" ? "For Rent" : "For Sale"}
                        </TypeTag>
                      </EquipMeta>
                    </EquipInfo>
                    <ChevronRight size={16} color="rgba(23,23,23,0.3)" />
                  </EquipmentRow>
                ))
              )}
            </EquipmentList>
          </>
        )}

        {step === "configure" && selected && (
          <>
            <ModalBody as="form" id="publish-equipment-form" onSubmit={handlePublish}>
              <FormCard>
                <CardLabel>Listing details</CardLabel>
                <Field>
                  <Label htmlFor="pub-title">
                    <FormLabelIcon icon={Tag} />
                    Listing Title
                  </Label>
                  <FieldInput
                    id="pub-title"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder={selected.name}
                  />
                </Field>
                <Field>
                  <Label htmlFor="pub-desc">
                    <FormLabelIcon icon={FileText} />
                    Description
                  </Label>
                  <FieldTextArea
                    id="pub-desc"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    rows={2}
                    placeholder="Optional description for buyers..."
                  />
                </Field>
              </FormCard>

              <FormCard>
                <CardLabel>Pricing & availability</CardLabel>
                <FieldGrid>
                  <Field>
                    <Label htmlFor="pub-price">
                      <FormLabelIcon icon={IndianRupee} />
                      Price (₹) *
                    </Label>
                    <FieldInput
                      id="pub-price"
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                      placeholder="e.g. 5000"
                      required
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="pub-unit">Price Unit</Label>
                    <FieldSelect
                      id="pub-unit"
                      value={form.price_unit}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, price_unit: e.target.value as PriceUnit }))
                      }
                    >
                      <option value="fixed">Fixed</option>
                      <option value="per_day">Per Day</option>
                      <option value="per_hour">Per Hour</option>
                      <option value="negotiable">Negotiable</option>
                    </FieldSelect>
                  </Field>
                </FieldGrid>

                <FieldGrid>
                  <Field>
                    <Label htmlFor="pub-mode">Booking Mode</Label>
                    <FieldSelect
                      id="pub-mode"
                      value={form.booking_mode}
                      disabled
                      onChange={() => {}}
                    >
                      <option value="inquiry_only">Inquiry Only</option>
                    </FieldSelect>
                  </Field>
                  <Field>
                    <Label htmlFor="pub-location">
                      <FormLabelIcon icon={MapPin} />
                      Location
                    </Label>
                    <FieldInput
                      id="pub-location"
                      value={form.location}
                      onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                      placeholder="e.g. Mumbai"
                    />
                  </Field>
                </FieldGrid>

                <Field>
                  <Label htmlFor="pub-status">Publish Status</Label>
                  <FieldSelect
                    id="pub-status"
                    value={form.status}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, status: e.target.value as ListingStatus }))
                    }
                  >
                    <option value="active">Active — visible immediately</option>
                    <option value="draft">Draft — save for later</option>
                  </FieldSelect>
                </Field>
              </FormCard>
            </ModalBody>

            <ModalActions style={{ flexDirection: "row", gap: 10 }}>
              <SecondaryButton
                type="button"
                onClick={() => setStep("pick")}
                style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              >
                <ArrowLeft size={15} /> Back
              </SecondaryButton>
              <PrimaryButton
                type="submit"
                form="publish-equipment-form"
                disabled={submitting}
                style={{ flex: 1.4 }}
              >
                {submitting ? (
                  <>
                    <Spinner size={16} /> Publishing…
                  </>
                ) : (
                  "Publish to Marketplace"
                )}
              </PrimaryButton>
            </ModalActions>
          </>
        )}
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

export default PublishFromEquipmentModal;

const ModalShell = styled(BaseModalShell)<{ $maxWidth?: number }>`
  max-width: ${(p) => p.$maxWidth ?? 760}px;
`;

const PickToolbar = styled.div`
  padding: 16px 24px 0;
  flex-shrink: 0;
`;

const EquipmentList = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 14px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 48px 24px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.45);
  line-height: 1.6;
`;

const EmptyTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const EmptyDesc = styled.div`
  max-width: 320px;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.5);
`;

const EquipmentRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  background: #fafafa;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.04);
    border-color: rgba(201, 77, 170, 0.22);
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(201, 77, 170, 0.08);
  }
`;

const EquipIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(124, 77, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const EquipInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const EquipName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  margin-bottom: 2px;
`;

const EquipMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.5);
`;

const TypeTag = styled.span<{ $type: string }>`
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  background: ${(p) => (p.$type === "rent" ? "#0ea5e918" : "#7c4dff18")};
  color: ${(p) => (p.$type === "rent" ? "#0ea5e9" : "#7c4dff")};
`;
